"use client";

import Link from "@/components/Link";
import { useAuth } from "@clerk/nextjs";
import { useKurboAction } from "@/hooks/useKurboAction";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";
import { formatCompactNumber } from "@/lib/shared/currency";
import { formatNumber } from "@/lib/shared/utils";
import styles from "./styles.module.scss";
import {
  Box as MantineBox,
  Text as MantineText,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import { IconArrowRight, IconBoltFilled } from "@tabler/icons-react";

type Props = {
  count: number;
};

const KurboPlaceholderCard = ({ count }: Props) => {
  const { handleKurboAction } = useKurboAction();
  const { isSignedIn } = useAuth();
  const tokensRemaining = useTokenBudgetStore((s) => s.tokensRemaining);
  const tokensInitialized = useTokenBudgetStore((s) => s.initialized);

  const displayCount = formatCompactNumber(count);
  const listingText = `${displayCount} listing${count !== 1 ? "s" : ""}`;
  const accessibleListingText = `${formatNumber(count)} listing${count !== 1 ? "s" : ""}`;

  if (!TOKEN_UI_ENABLED) return null;

  const isOutOfTokens = isSignedIn && tokensInitialized && tokensRemaining === 0;
  const panelContent = (
    <>
      <MantineBox component="span" className={styles.signal} aria-hidden="true">
        <IconBoltFilled size={18} />
      </MantineBox>
      <MantineBox component="span" className={styles.copy}>
        <MantineText component="span" className={styles.eyebrow}>
          KURBO MATCH
        </MantineText>
        <MantineText component="span" className={styles.headline}>
          <MantineText component="span" className={styles.mobileHeadline}>
            {displayCount} new {count === 1 ? "listing" : "listings"}
          </MantineText>
          <MantineText component="span" className={styles.desktopHeadline}>
            {listingText} landed in the last 24 hours
          </MantineText>
        </MantineText>
        <MantineText component="span" className={styles.supportingText}>
          {isOutOfTokens
            ? "Upgrade to view recent drops and keep watching the market."
            : "Enable KURBO mode to view them."}
        </MantineText>
      </MantineBox>
      <MantineBox component="span" className={styles.cta}>
        {isOutOfTokens ? "Get KURB Pro" : "Enable KURBO"}
        <IconArrowRight size={17} stroke={2} aria-hidden="true" />
      </MantineBox>
    </>
  );

  if (isOutOfTokens) {
    return (
      <MantineBox className={styles.stickyShell}>
        <Link
          href="/plans"
          className={`${styles.panel} ${styles.upgradePanel}`}
          aria-label={`Get KURB Pro to view ${accessibleListingText} added in the last 24 hours`}
        >
          {panelContent}
        </Link>
      </MantineBox>
    );
  }

  return (
    <MantineBox className={styles.stickyShell}>
      <MantineUnstyledButton
        className={styles.panel}
        onClick={handleKurboAction}
        type="button"
        aria-label={`Enable KURBO mode to view ${accessibleListingText} added in the last 24 hours`}
      >
        {panelContent}
      </MantineUnstyledButton>
    </MantineBox>
  );
};

export default KurboPlaceholderCard;
